import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CalendarClock, Book, Pickaxe, GraduationCap } from 'lucide-react';
import { motion } from 'framer-motion';

interface ChronologyPreferenceSelectionProps {
    value: BiblicalChronology;
    onChange: (value: BiblicalChronology) => void;
}

export function ChronologyPreferenceSelection({ value, onChange }: ChronologyPreferenceSelectionProps) {
    const options: { id: BiblicalChronology; label: string; description: string; icon: any; accent: string }[] = [
        {
            id: 'Textual',
            label: 'Textual Chronology',
            description: 'Strict adherence to internal biblical numbers (Masoretic Text). Anchors the Exodus to 1446 BC and treats the Bible as its own independent clock.',
            icon: Book,
            accent: 'text-blue-500'
        },
        {
            id: 'Archaeological',
            label: 'Archaeological Synchrony',
            description: 'Prioritizes harmonizing the text with Ancient Near Eastern records and destruction layers. Anchors the Exodus to c. 1250 BC.',
            icon: Pickaxe,
            accent: 'text-amber-500'
        },
        {
            id: 'Critical',
            label: 'Critical Scholarship',
            description: 'Follows secular consensus, often prioritizing composition dates over narrative settings. Analyzes the text through the lens of modern academic history.',
            icon: GraduationCap,
            accent: 'text-purple-500'
        }
    ];

    return (
        <Card className="bg-background/40 backdrop-blur-md border-border/40 shadow-xl overflow-hidden rounded-[2rem]">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -mr-32 -mt-32 blur-3xl pointer-events-none" />

            <CardHeader className="pb-8 relative z-10">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-primary/10 rounded-2xl border border-primary/20">
                        <CalendarClock className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                        <CardTitle className="text-2xl font-bold tracking-tight">Chronological Framework</CardTitle>
                        <CardDescription className="text-muted-foreground/80 font-medium">
                            Choose the archaeological and historical lens through which dates are presented.
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="relative z-10">
                <RadioGroup
                    value={value}
                    onValueChange={(v) => onChange(v as BiblicalChronology)}
                    className="grid grid-cols-1 lg:grid-cols-3 gap-6"
                >
                    {options.map((option, index) => (
                        <motion.div
                            key={option.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <RadioGroupItem
                                value={option.id}
                                id={`chrono-${option.id}`}
                                className="peer sr-only"
                            />
                            <Label
                                htmlFor={`chrono-${option.id}`}
                                className="flex flex-col items-start justify-between rounded-2xl border-2 border-border/40 bg-background/30 p-6 hover:bg-background/60 hover:border-primary/40 hover:shadow-lg backdrop-blur-sm peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 peer-data-[state=checked]:shadow-[0_0_20px_rgba(var(--primary),0.1)] cursor-pointer h-full transition-all group"
                            >
                                <div className="flex flex-col gap-4 w-full">
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-background/50 border border-border/20 group-hover:scale-110 transition-transform ${option.accent}`}>
                                        <option.icon className="h-6 w-6" />
                                    </div>
                                    <div className="space-y-1">
                                        <span className="font-bold text-lg block tracking-tight group-hover:text-primary transition-colors">{option.label}</span>
                                        <div className="h-0.5 w-8 bg-primary/30 rounded-full group-hover:w-16 transition-all" />
                                    </div>
                                    <span className="text-sm text-muted-foreground leading-relaxed">
                                        {option.description}
                                    </span>
                                </div>
                            </Label>
                        </motion.div>
                    ))}
                </RadioGroup>
            </CardContent>
        </Card>
    );
}
